import type { FinanceCategory } from './categories.ts';
import type { Transaction } from "./finance.ts";
import { calculateProfitLoss, type FinancialAccount, type CategoryFinancialMapping } from "./profit-loss.ts";

export type PeriodKey = "all" | "today" | "7d" | "30d" | "month" | "lastMonth" | "3m" | "year" | "custom";
export type ChartGranularity = "auto" | "daily" | "weekly" | "monthly";
export type DateRange = { start: string; end: string };

function iso(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function atMidnight(value: Date) {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function addDays(value: Date, days: number) {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

export function periodDateRange(period: PeriodKey, startDate = "", endDate = "", now = new Date()): DateRange | null {
  const today = atMidnight(now);
  if (period === "all") return null;
  if (period === "today") return { start: iso(today), end: iso(today) };
  if (period === "7d") return { start: iso(addDays(today, -6)), end: iso(today) };
  if (period === "30d") return { start: iso(addDays(today, -29)), end: iso(today) };
  if (period === "month") return { start: iso(new Date(today.getFullYear(), today.getMonth(), 1)), end: iso(today) };
  if (period === "lastMonth") {
    return {
      start: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      end: iso(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
  }
  if (period === "3m") return { start: iso(new Date(today.getFullYear(), today.getMonth() - 2, 1)), end: iso(today) };
  if (period === "year") return { start: iso(new Date(today.getFullYear(), 0, 1)), end: iso(today) };
  if (!startDate && !endDate) return null;
  return { start: startDate || "0000-01-01", end: endDate || "9999-12-31" };
}

export function previousDateRange(range: DateRange | null): DateRange | null {
  if (!range || range.start.startsWith("0000") || range.end.startsWith("9999")) return null;
  const start = new Date(`${range.start}T00:00:00`);
  const end = new Date(`${range.end}T00:00:00`);
  const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
  const previousEnd = addDays(start, -1);
  return { start: iso(addDays(previousEnd, -(days - 1))), end: iso(previousEnd) };
}

export function dateInRange(date: string, range: DateRange | null) {
  return !range || (date >= range.start && date <= range.end);
}

function weekStart(date: string) {
  const value = new Date(`${date}T00:00:00`);
  const day = value.getDay() || 7;
  return iso(addDays(value, 1 - day));
}

export function resolvedGranularity(granularity: ChartGranularity, dates: string[]): Exclude<ChartGranularity, "auto"> {
  if (granularity !== "auto") return granularity;
  const availableDates = dates.filter(Boolean);
  if (availableDates.length < 2) return "daily";
  let minimum = availableDates[0], maximum = availableDates[0];
  for (const date of availableDates) { if (date < minimum) minimum = date; if (date > maximum) maximum = date; }
  const first = new Date(`${minimum}T00:00:00`);
  const last = new Date(`${maximum}T00:00:00`);
  const span = Math.round((last.getTime() - first.getTime()) / 86_400_000) + 1;
  if (span <= 45) return "daily";
  if (span <= 180) return "weekly";
  return "monthly";
}

export type CashActivityPoint = { period: string; income: number; expense: number; netCashflow: number; transactionCount: number };

/** Bank activity does not require category or report-account mappings. */
export function aggregateCashActivity(transactions: Transaction[], granularity: ChartGranularity) {
  const seen = new Set<string>();
  const eligible: Transaction[] = [];
  let missingDateCount = 0, missingDirectionCount = 0;
  for (const row of transactions) {
    if (row.deletedAt || seen.has(row.id)) continue;
    seen.add(row.id);
    if (!row.date) { missingDateCount += 1; continue; }
    if (!row.direction) { missingDirectionCount += 1; continue; }
    eligible.push(row);
  }
  const resolution = resolvedGranularity(granularity, eligible.map((row) => row.date));
  const buckets = new Map<string, CashActivityPoint>();
  for (const row of eligible) {
    const period = resolution === "daily" ? row.date : resolution === "weekly" ? weekStart(row.date) : row.date.slice(0, 7);
    const point = buckets.get(period) || { period, income: 0, expense: 0, netCashflow: 0, transactionCount: 0 };
    if (row.direction === "CREDIT") point.income += row.amount;
    if (row.direction === "DEBIT") point.expense += row.amount;
    point.netCashflow = point.income - point.expense;
    point.transactionCount += 1;
    buckets.set(period, point);
  }
  return { data: [...buckets.values()].sort((a, b) => a.period.localeCompare(b.period)), resolution,
    transactionCount: eligible.length, missingDateCount, missingDirectionCount };
}

export function aggregateFinancialSeries(
  transactions: Transaction[],
  accounts: FinancialAccount[],
  granularity: ChartGranularity,
  mappings: CategoryFinancialMapping[] = [], categories: FinanceCategory[] = [],
) {
  const datedTransactions = transactions.filter((item) => Boolean(item.date));
  const resolved = resolvedGranularity(granularity, datedTransactions.map((item) => item.date));
  const grouped = new Map<string, Transaction[]>();
  for (const transaction of datedTransactions) {
    const bucket = resolved === "daily" ? transaction.date : resolved === "weekly" ? weekStart(transaction.date) : transaction.date.slice(0, 7);
    const rows = grouped.get(bucket) || [];
    rows.push(transaction);
    grouped.set(bucket, rows);
  }
  return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([period, rows]) => {
    const report = calculateProfitLoss(rows, accounts, [], mappings, categories);
    return { period, income: report.income, expense: report.expense, grossProfit: report.grossProfit, transactionCount: rows.length };
  });
}

export function comparisonPercent(current: number, previous: number) {
  if (!previous) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function calendarMonthRange(month: string): DateRange | null {
  const match = month.match(/^(\d{4})-(\d{2})/);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11) return null;
  return {
    start: iso(new Date(year, monthIndex, 1)),
    end: iso(new Date(year, monthIndex + 1, 0)),
  };
}

export function previousCalendarMonth(month: string) {
  const range = calendarMonthRange(month);
  if (!range) return "";
  const start = new Date(`${range.start}T00:00:00`);
  return iso(new Date(start.getFullYear(), start.getMonth() - 1, 1)).slice(0, 7);
}

export function sixMonthFinancialHistory(
  transactions: Transaction[],
  accounts: FinancialAccount[],
  endMonth: string,
  mappings: CategoryFinancialMapping[] = [], categories: FinanceCategory[] = [],
) {
  const range = calendarMonthRange(endMonth);
  if (!range) return [];
  const end = new Date(`${range.start}T00:00:00`);
  return Array.from({ length: 6 }, (_, index) => {
    const monthStart = new Date(end.getFullYear(), end.getMonth() - (5 - index), 1);
    const month = iso(monthStart).slice(0, 7);
    const report = calculateProfitLoss(
      transactions.filter((item) => item.date.slice(0, 7) === month),
      accounts, [], mappings, categories,
    );
    return {
      month,
      income: report.income,
      expense: report.expense,
      net: report.netProfit,
      transactionCount: report.transactionCount,
    };
  });
}
